import React, { useCallback, useEffect, useState } from 'react';
import { App, Button, Tag } from 'antd';
import { useNavigate } from 'react-router-dom';

import DataState, { deriveDataState } from '../loading/DataState';
import Skeleton from '../loading/Skeleton';
import { confirmDestructive } from '../common/confirmDestructive';
import { formatDisplayDateTime } from '../../utils/dateDisplay';
import { apiErrorMessage } from '../../utils/apiErrors';
import useDateFormat from '../../hooks/useDateFormat';
import { endDeviceSession, listDeviceSessions } from '../../services/deviceSessions';

const PLATFORM_LABELS = { ios: 'iPhone or iPad', android: 'Android' };

/** "iPhone or iPad · keepr 1.0.0", from what the device said when it signed in. */
export function deviceDetail({ platform, appVersion }) {
	const kind = PLATFORM_LABELS[platform] || 'Phone app';
	return appVersion ? `${kind} · keepr ${appVersion}` : kind;
}

/**
 * Account settings › Devices (KPR-1674; docs/plans/ios-app.md, Decision 4):
 * the phone apps signed in to this account on a device session, the one in
 * hand marked This device, and a sign-out on each (red, confirmed, last, as
 * the action taxonomy has it): "Sign out this device" on the one in hand,
 * "Sign out" on every other, whose dialog names it. Shown on every platform: a browser
 * holds no device session itself, but it is where a lost phone is signed out
 * from. Signing out the device in hand is the ordinary sign-out (/logout,
 * which also ends its device session on the server).
 */
export default function DevicesSection() {
	const { message, modal } = App.useApp();
	const navigate = useNavigate();
	useDateFormat();

	const [devices, setDevices] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	const load = useCallback(() => {
		let active = true;
		setLoading(true);
		listDeviceSessions()
			.then(rows => { if (active) { setDevices(rows); setError(null); } })
			.catch(failure => { if (active) setError(apiErrorMessage(failure)); })
			.finally(() => { if (active) setLoading(false); });
		return () => { active = false; };
	}, []);

	useEffect(load, [load]);

	const signOut = useCallback((row) => {
		confirmDestructive(modal, {
			title: row.current ? 'Sign out this device?' : `Sign out ${row.deviceName}?`,
			content: row.current
				? 'You are signed out of keepr on this device. You can sign in again right away.'
				: `${row.deviceName} is signed out of keepr. It has to sign in again to open keepr.`,
			okText: 'Sign out',
			onOk: async () => {
				if (row.current) {
					navigate('/logout');
					return;
				}
				try {
					await endDeviceSession(row.id);
					setDevices(rows => (rows || []).filter(device => device.id !== row.id));
					message.success(`${row.deviceName} is signed out.`);
				} catch (failure) {
					message.error(apiErrorMessage(failure, 'Could not sign that device out.'));
				}
			},
		});
	}, [message, modal, navigate]);

	const hasData = Array.isArray(devices);
	const isEmpty = hasData && devices.length === 0;

	return (
		<section className="mt-4 form-panel devices-section" aria-labelledby="devices-section-title">
			<h3 id="devices-section-title">Devices</h3>
			<p className="text-muted small">Phones signed in to keepr through the app. Each stays signed in until it is signed out here or goes 90 days unused.</p>
			<DataState
				state={deriveDataState({ loading, error, hasData, isEmpty })}
				hasData={hasData}
				isEmpty={isEmpty}
				skeleton={<Skeleton variant="list-row" count={2} />}
				empty={<p className="text-muted small mb-0 devices-section__empty">No phones are signed in. Sign in on the keepr app and the phone appears here.</p>}
				error={error}
				onRetry={load}
				label="your devices"
			>
				<ul className="devices-section__list">
					{(devices || []).map(row => (
						<li key={row.id} className="devices-section__device">
							<div className="devices-section__about">
								<span className="devices-section__name">
									{row.deviceName}
									{row.current ? <Tag className="devices-section__current">This device</Tag> : null}
								</span>
								<span className="devices-section__meta">
									{deviceDetail(row)}
									{row.lastSeenAt ? ` · last used ${formatDisplayDateTime(row.lastSeenAt)}` : ''}
								</span>
							</div>
							<Button danger className="devices-section__sign-out" onClick={() => signOut(row)} aria-label={`Sign out ${row.deviceName}`}>
								{row.current ? 'Sign out this device' : 'Sign out'}
							</Button>
						</li>
					))}
				</ul>
			</DataState>
		</section>
	);
}
